import { getEmployeeIdFromToken, getUserAccountIdFromToken } from '../../services/DecodeToken';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userApi } from '../api/axiosInstance';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-hot-toast';
import { clearAuthStorage } from '../../utils/authStorage';
import { endServerSession, getDeviceId, storeRefreshToken, type RefreshResponse } from '../api/tokenRefresh';
import { t } from '../../i18n/t';
import {
  getUserProfile,
  getUserById as getUserByIdService,
  updateProfile,
  updatePassword as updatePasswordService,
  deleteUser as deleteUserService,
  verifyEmail,
  sendResetLink,
  resetPassword as resetPasswordService,
  addCandidateSignup,
  selfUpdatePassword as selfUpdatePasswordService,
} from '../../services/AdminServices/userManagementServices/userService';


interface User {
  user_id: number;
  name: string;
  email: string;
  contactNumber?: string;
  imageUrl?: string | null;
  isEmailVerified?: boolean;
  role: string;
  permission: string[];
  isActive?: boolean;
  isDefaultPass?: boolean;
  [key: string]: any;
}


interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  error: any;
  isAuthenticated: boolean;
  signupStatus: string | null;
  otpVerified: boolean;
  passwordResetStatus: string | null;
  passwordResetRequestStatus: string | null;
  /** Deduplicated permission strings from JWT — UI-layer only */
  permissions: string[];
  /** primarysid from JWT — employee HR record ID (for EM/Payroll APIs) */
  employeeId: number | null;
  /** nameid from JWT — user account ID (for UM APIs) */
  userId: number | null;
}

/**
 * The signed-in user, restored from where login already put them.
 *
 * `user` started as `null` on every page load and was only ever filled by a login RESPONSE, so
 * a refresh emptied it - while `localStorage.userDetails` and `localStorage.token`, both
 * written by that same login, sat there untouched and were already read directly by other code
 * (`SidebarMenu`'s `getIsManager`, and this slice's own merge branches).
 *
 * What that cost, seen on a real candidate session: refresh "My applications" and it says "You
 * haven't applied to any jobs yet" over a live application, because the screen keys on
 * `user.candidate_id`; and the header greets them as "User". Every screen keyed on anything in
 * `user` had the same hole.
 *
 * Reading the token here does not widen anything: `axiosInstance` already sends
 * `localStorage.token` on every request and `ProtectedRoute` already decodes it, so this only
 * stops Redux from disagreeing with what the rest of the app already believes. An expired
 * token fails at the API exactly as it does today, and the 401 interceptor clears the session.
 */
const restoredUser = (): User | null => {
  try {
    const raw = localStorage.getItem('userDetails');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0 ? parsed : null;
  } catch {
    // Corrupt or half-written JSON is the same as no session: start clean rather than throw
    // while the store is being created, which would take the whole app down.
    return null;
  }
};

const restoredToken = (): string | null => {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
};

const initialToken = restoredToken();

/**
 * Seed employeeId / userId from the stored token so a page reload does not leave them null.
 * Before this, a reload restored `user` from localStorage, App.tsx then skipped /Login/Me,
 * and employeeId stayed null for the whole session - which hid every dashboard widget that
 * needs an employee record (My clock, leave balance, ...) until the user signed in again.
 * /Login/Me still runs on load and overwrites these with the server's answer.
 */
const positiveIdFromToken = (read: () => string | null): number | null => {
  try {
    const n = Number(read());
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const initialState: AuthState = {
  user: restoredUser(),
  token: initialToken,
  loading: false,
  error: null,
  isAuthenticated: !!initialToken,
  signupStatus: null,
  otpVerified: false,
  passwordResetStatus: null,
  passwordResetRequestStatus: null,
  permissions: [],
  employeeId: initialToken ? positiveIdFromToken(getEmployeeIdFromToken) : null,
  userId: initialToken ? positiveIdFromToken(getUserAccountIdFromToken) : null,
};

const normalizeRoles = (role: any): { role: string; roles: string[] } => {
  if (!role) return { role: '', roles: [] };

  if (typeof role === 'string') {
    const roles = role.split(',').map(r => r.trim()).filter(Boolean);
    return { role: roles[0] || '', roles };
  }

  if (Array.isArray(role)) {
    const roles = role.map(String).map(r => r.trim()).filter(Boolean);
    return { role: roles[0] || '', roles };
  }

  return { role: String(role), roles: [String(role)] };
};


/** Turns a normal login response ({token, refreshToken, sessionToken}) into the sign-in payload. */
const buildLoginResult = (data: RefreshResponse & { sessionToken: string; [k: string]: any }): { token: string; sessionToken: string; userData: User } => {
    storeRefreshToken(data.refreshToken);
    const decoded: any = jwtDecode(data.token);

    const { role, roles } = normalizeRoles(decoded.role);

    const userData: User = {
      user_id: Number(decoded.nameid),
      email: decoded.email,
      name: decoded.name || decoded.email.split('@')[0],
      role,
      roles,
      permission: Array.isArray(decoded.permission) ? decoded.permission : [decoded.permission].filter(Boolean),
      organizationId: decoded.organizationId || decoded.OrganizationId,
      isDefaultPass: false,
      employee_Id: null,
    };

  return { token: data.token, sessionToken: data.sessionToken, userData };
};

type LoginResult = { token: string; sessionToken: string; userData: User };
/** Rejection payload when UM asks for a second factor instead of issuing tokens (docs/85 section 9). */
export type MfaChallenge = { mfaRequired: true; challengeToken: string };
export const isMfaChallenge = (v: any): v is MfaChallenge => !!v && v.mfaRequired === true && typeof v.challengeToken === 'string';
/** Policy requires MFA and the user has none: UM answers with a challenge to enrol instead of tokens. */
export type EnrolmentChallenge = { mfaEnrolmentRequired: true; challengeToken: string };
export const isEnrolmentChallenge = (v: any): v is EnrolmentChallenge => !!v && v.mfaEnrolmentRequired === true && typeof v.challengeToken === 'string';
/** Either kind of pre-token answer; the login screens branch on which. */
export const isPreTokenChallenge = (v: any): boolean => isMfaChallenge(v) || isEnrolmentChallenge(v);

export const loginUser = createAsyncThunk<
  { token: string; sessionToken: string; userData: User },
  { email: string; password: string; tenant?: string },
  { rejectValue: any }
>('auth/loginUser', async (credentials, { rejectWithValue }) => {
  try {
    // tenant is sent only on a tenant host; the platform host and local dev send exactly what they always did.
    // deviceId is what makes UM issue a refresh token (access JWTs are short-lived).
    const deviceId = getDeviceId();
    const body = credentials.tenant
      ? { ...credentials, deviceId }
      : { email: credentials.email, password: credentials.password, deviceId };
    const { data } = await userApi.post('Login/login', body);
    if (data?.mfaRequired) return rejectWithValue({ mfaRequired: true, challengeToken: data.challengeToken });
    if (data?.mfaEnrolmentRequired) return rejectWithValue({ mfaEnrolmentRequired: true, challengeToken: data.challengeToken });
    return buildLoginResult(data);
  } catch (err: any) {
    console.error('❌ Login error details:', err);
    const errorData = err.response?.data;
    // One answer for unknown workspace, unknown user and wrong password (docs/85): localised here.
    if (errorData?.code === 'INVALID_CREDENTIALS') {
      return rejectWithValue(t('auth:login.invalidForWorkspace'));
    }
    if (errorData?.code === 'SSO_REQUIRED') {
      return rejectWithValue(t('auth:sso.errors.SSO_REQUIRED'));
    }
    const errorMessage = errorData?.details ||
      errorData?.error ||
      errorData?.message ||
      err.message ||
      'Authentication failed';

    console.error('❌ Extracted error message:', errorMessage);
    return rejectWithValue(errorMessage);
  }
});

const authFailure = (err: any, rejectWithValue: (v: any) => any) => {
  const d = err?.response?.data;
  return rejectWithValue(d?.details || d?.error || d?.message || err?.message || 'Authentication failed');
};

/** Second step of a login that answered mfaRequired: TOTP code OR a recovery code. */
export const loginMfa = createAsyncThunk<
  LoginResult,
  { challengeToken: string; code?: string; recoveryCode?: string; tenant?: string },
  { rejectValue: any }
>('auth/loginMfa', async (body, { rejectWithValue }) => {
  try {
    // tenant (host slug) and deviceId ride along, exactly as on Login/login.
    const { tenant, ...rest } = body;
    const { data } = await userApi.post('Login/mfa', { ...rest, ...(tenant ? { tenant } : {}), deviceId: getDeviceId() });
    return buildLoginResult(data);
  } catch (err: any) {
    return authFailure(err, rejectWithValue);
  }
});

/** Completes an SSO sign-in: trades the one-time code for the normal login response. */
export const ssoExchange = createAsyncThunk<LoginResult, { code: string; tenant?: string } | string, { rejectValue: any }>(
  'auth/ssoExchange',
  async (arg, { rejectWithValue }) => {
    try {
      const { code, tenant } = typeof arg === 'string' ? { code: arg, tenant: undefined } : arg;
      const { data } = await userApi.post('sso/exchange', { code, ...(tenant ? { tenant } : {}), deviceId: getDeviceId() });
      if (data?.mfaRequired) return rejectWithValue({ mfaRequired: true, challengeToken: data.challengeToken });
      if (data?.mfaEnrolmentRequired) return rejectWithValue({ mfaEnrolmentRequired: true, challengeToken: data.challengeToken });
      return buildLoginResult(data);
    } catch (err: any) {
      // Keep the server's code so the landing page can say what to do next, not just that it failed.
      return rejectWithValue({ ssoError: err?.response?.data?.code ?? null });
    }
  },
);

/** After forced enrolment UM returns the normal login response inside the verify answer: finish exactly as a login. */
export const completeEnrolmentLogin = createAsyncThunk<LoginResult, any, { rejectValue: any }>(
  'auth/completeEnrolmentLogin',
  async (login, { rejectWithValue }) => {
    try { return buildLoginResult(login); } catch (err: any) { return authFailure(err, rejectWithValue); }
  },
);

export const fetchCurrentUser = createAsyncThunk<
  { token: string; userData: User },
  string,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (sessionToken, { rejectWithValue }) => {
  try {
    const { data } = await userApi.post('Login/Me', { sessionToken });
    const decoded: any = jwtDecode(data.token);

    const { role, roles } = normalizeRoles(data.userData?.roles || data.userData?.role || decoded.role);

    const userData: User = {
      user_id: data.userData?.user_Id || Number(decoded.nameid),
      email: data.userData?.email,
      name: data.userData?.name || decoded.email?.split('@')[0],
      role,
      roles,
      permission: data.userData?.permissions || decoded.permission || [],
      imageUrl: data.userData?.imageUrl,
      candidate_id: data.userData?.candidate_id || decoded.candidate_id,
      isTeamLead: data.userData?.isTeamLead || false,
      organizationId: data.userData?.organizationId || data.userData?.OrganizationId || decoded.organizationId || decoded.OrganizationId,
      isDefaultPass: data.userData?.isDefaultPass ?? decoded.isDefaultPass ?? false,
      employee_Id: data.userData?.employee_Id ?? null,
      // Composable dashboard requiresLineAuthority gate (design spec 2.3/3.6).
      // The ONLY permitted source is UM's PositionalRoleService.HasLineAuthorityAsync,
      // additively surfaced on this same /Login/Me response. Fail closed: strict
      // equality to true, not a truthy check, so a missing field (older UM build
      // that has not shipped this yet) or any non-boolean value resolves to false
      // rather than accidentally granting team-widget visibility. Never substitute
      // isManager/isTeamLead above, which disagree with the real manager_id
      // hierarchy for 7 of 45 flagged people (CLAUDE.md forbidden-input rule).
      hasLineAuthority: data.userData?.hasLineAuthority === true,
    };

    return { token: data.token, userData };
  } catch {
    return rejectWithValue('Session expired');
  }
});

// registerUser: signs up a new candidate via userService
export const registerUser = createAsyncThunk<any, Record<string, any>, { rejectValue: any }>(
  'auth/registerUser',
  async (userData, { rejectWithValue }) => {
    try {
      const data = await addCandidateSignup(userData as any);
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Registration failed';
      return rejectWithValue(msg);
    }
  }
);

// verifyOtp: verifies email OTP via userService
export const verifyOtp = createAsyncThunk<any, { email: string; otp: string; tenant?: string }, { rejectValue: string }>(
  'auth/verifyOtp',
  async ({ email, otp, tenant }, { rejectWithValue }) => {
    try {
      const data = await verifyEmail({ email, otp, ...(tenant ? { tenant } : {}) });
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'OTP verification failed';
      return rejectWithValue(msg);
    }
  }
);

// requestPasswordReset: sends password reset link via userService
export const requestPasswordReset = createAsyncThunk<any, { email: string; tenant?: string }, { rejectValue: string }>(
  'auth/requestPasswordReset',
  async ({ email, tenant }, { rejectWithValue }) => {
    try {
      const data = await sendResetLink({ email, ...(tenant ? { tenant } : {}) });
      return data;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to send reset link';
      return rejectWithValue(msg);
    }
  }
);

// passwordReset: resets password via userService (Swagger: POST /Users/reset-password)
export const passwordReset = createAsyncThunk<
  any,
  { email: string; token: string; newPassword: string; tenant?: string },
  { rejectValue: string }
>('auth/passwordReset', async ({ email, token, newPassword, tenant }, { rejectWithValue }) => {
  try {
    const data = await resetPasswordService({ email, token, newPassword, ...(tenant ? { tenant } : {}) });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Password reset failed';
    return rejectWithValue(msg);
  }
});
export const getUserById = createAsyncThunk<User, number, { rejectValue: string }>(
  'auth/getUserById',
  async (userId, { rejectWithValue }) => {
    try {
      const data = await getUserByIdService(userId);
      const user: User = {
        user_id: data.userId,
        name: data.userName,
        email: data.email,
        contactNumber: data.phoneNumber,
        imageUrl: data.imageUrl,
        isEmailVerified: data.isEmailverified,
        role: data.role,
        permission: data.permissions || [],
        isActive: data.isActive,
        isDefaultPass: data.isDefaultPass ?? false,
      };
      return user;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to fetch user';
      return rejectWithValue(msg);
    }
  }
);
export const getCurrentUserProfile = createAsyncThunk<User, void, { rejectValue: string }>(
  'auth/getCurrentUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await getUserProfile();
      // data.role is this endpoint's single field — possibly a comma-joined
      // string for a multi-role account, same shape loginUser/fetchCurrentUser
      // normalize elsewhere. Doing it here too means the merge below actually
      // has a `roles` array to merge, instead of leaving the existing one
      // untouched by omission (which would happen to work) or silently
      // overwriting it with undefined (which is what happened before this
      // endpoint's response was merged rather than replacing state.user
      // outright — see the .fulfilled reducer).
      const { role, roles } = normalizeRoles(data.role);
      const user: User = {
        user_id: data.userId,
        name: data.userName,
        email: data.email,
        contactNumber: data.phoneNumber,
        imageUrl: data.imageUrl,
        isEmailVerified: data.isEmailverified,
        role,
        roles,
        permission: data.permissions || [],
        isActive: data.isActive,
        isDefaultPass: data.isDefaultPass ?? false,
      };
      return user;
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to fetch profile';
      return rejectWithValue(msg);
    }
  }
);
export const updateUser = createAsyncThunk<
  any,
  { Id: string; FullName: string; contactNumber: string; ProfileImage?: File },
  { rejectValue: string }
>('auth/updateUser', async ({ Id, FullName, contactNumber, ProfileImage }, { rejectWithValue }) => {
  try {
    const data = await updateProfile({
      id: Number(Id),
      name: FullName,
      contactNumber,
      profileImage: ProfileImage,
    });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update profile';
    return rejectWithValue(msg);
  }
});
export const updatePasswordByUser = createAsyncThunk<
  any,
  { userId: number; newPassword: string; confirmPassword: string },
  { rejectValue: string }
>('auth/updatePasswordByUser', async ({ userId, newPassword, confirmPassword }, { rejectWithValue }) => {
  try {
    const data = await updatePasswordService({ userId, newPassword, confirmPassword });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update password';
    return rejectWithValue(msg);
  }
});

export const selfUpdatePasswordByUser = createAsyncThunk<
  any,
  { userId: number; newPassword: string; currentPassword: string },
  { rejectValue: string }
>('auth/selfUpdatePasswordByUser', async ({ userId, newPassword, currentPassword }, { rejectWithValue }) => {
  try {
    const data = await selfUpdatePasswordService({ userId, newPassword, currentPassword });
    return data;
  } catch (err: any) {
    const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to update password';
    return rejectWithValue(msg);
  }
});

// deleteUser: deletes a user via userService
export const deleteUser = createAsyncThunk<{ userId: number }, number, { rejectValue: string }>(
  'auth/deleteUser',
  async (userId, { rejectWithValue }) => {
    try {
      await deleteUserService(userId);
      return { userId };
    } catch (err: any) {
      const msg = err?.response?.data?.details || err?.response?.data?.error || err?.message || 'Failed to delete user';
      return rejectWithValue(msg);
    }
  }
);


/** Shared by password, MFA and SSO sign-in: all three produce the same payload. */
const onSignedIn = (state: AuthState, action: { payload: LoginResult }) => {
    state.loading = false;
    state.user = action.payload.userData;
    state.token = action.payload.token;
    state.isAuthenticated = true;
    // Deduplicate permissions (backend encodes them twice in JWT - known bug)
    state.permissions = [...new Set(action.payload.userData.permission || [])];
    state.userId = action.payload.userData.user_id ?? null;
    // /Login/login returns userData: null - employee_Id is only available once
    // fetchCurrentUser (/Login/Me) resolves, which Login.tsx always awaits before navigating.
    state.employeeId = Number((action.payload.userData as any).employee_Id) > 0 ? Number((action.payload.userData as any).employee_Id) : null;
    const userDetails = {
      user_id: action.payload.userData.user_id,
      name: action.payload.userData.name,
      email: action.payload.userData.email,
      contactNumber: action.payload.userData.contactNumber,
      imageUrl: action.payload.userData.imageUrl,
      role: action.payload.userData.role,
      roles: action.payload.userData.roles,
      permission: action.payload.userData.permission,
      organizationId: action.payload.userData.organizationId,
      isEmailVerified: action.payload.userData.isEmailVerified,
      candidate_id: action.payload.userData.candidate_id,
      isDefaultPass: action.payload.userData.isDefaultPass,
    };
    localStorage.setItem('token', action.payload.token);
    localStorage.setItem('userDetails', JSON.stringify(userDetails));

};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.permissions = [];
      endServerSession();
      clearAuthStorage();
      toast.success('Logged out successfully');
    },
    // HRMS-90 (P4): live-refresh the UI permission set from GET /api/permissions/me/effective
    // (so permission changes apply without re-login; canonical Resource.Action names present).
    /** A refresh minted a new access token (possibly with changed claims). */
    tokenRefreshed(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
    setPermissions: (state, action: PayloadAction<string[]>) => {
      state.permissions = [...new Set(action.payload || [])];
    },
    clearError(state) {
      state.error = null;
    },
    resetOtpState(state) {
      state.otpVerified = false;
      state.signupStatus = null;
    },
    clearResetStatus(state) {
      state.passwordResetStatus = null;
      state.passwordResetRequestStatus = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, onSignedIn)
      .addCase(loginMfa.fulfilled, onSignedIn)
      .addCase(ssoExchange.fulfilled, onSignedIn)
      .addCase(completeEnrolmentLogin.fulfilled, onSignedIn)

      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        // A second-factor request is not a failure: Login.tsx moves to the code step.
        state.error = isPreTokenChallenge(action.payload) ? null : (action.payload || 'Authentication failed');
        // Don't show toast here - let the component handle it
      })

      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.userData;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        // Deduplicate permissions (backend encodes them twice in JWT - known bug)
        state.permissions = [...new Set(action.payload.userData.permission || [])];
        state.userId = action.payload.userData.user_id ?? null;
        state.employeeId = Number((action.payload.userData as any).employee_Id) > 0 ? Number((action.payload.userData as any).employee_Id) : null;
        const userDetails = {
          user_id: action.payload.userData.user_id,
          name: action.payload.userData.name,
          email: action.payload.userData.email,
          contactNumber: action.payload.userData.contactNumber,
          imageUrl: action.payload.userData.imageUrl,
          role: action.payload.userData.role,
          roles: action.payload.userData.roles,
          permission: action.payload.userData.permission,
          organizationId: action.payload.userData.organizationId,
          isEmailVerified: action.payload.userData.isEmailVerified,
          isTeamLead: action.payload.userData.isTeamLead,
          isDefaultPass: action.payload.userData.isDefaultPass,
          hasLineAuthority: (action.payload.userData as any).hasLineAuthority,
        };
        localStorage.setItem('userDetails', JSON.stringify(userDetails));
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        state.error = action.payload;
        // Only clear internal auth keys — preserve client-portal data (token, clientUser)
        // so that client-portal sessions aren't wiped when sessionToken refresh fails.
        const isClientPortal = !!localStorage.getItem('clientUser');
        if (isClientPortal) {
          localStorage.removeItem('sessionToken');
          localStorage.removeItem('userDetails');
        } else {
          clearAuthStorage();
        }
      })

      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.signupStatus = null;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
        state.signupStatus = action.payload.message || 'User registered. OTP sent.';
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.signupStatus = null;
        state.error = action.payload;
      })

      .addCase(verifyOtp.fulfilled, (state) => {
        state.otpVerified = true;
        state.loading = false;
      })
      .addCase(verifyOtp.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.otpVerified = false;
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(requestPasswordReset.fulfilled, (state, action) => {
        state.passwordResetRequestStatus = action.payload.message;
        state.loading = false;
      })
      .addCase(requestPasswordReset.rejected, (state, action) => {
        state.passwordResetRequestStatus = null;
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(passwordReset.fulfilled, (state, action) => {
        state.passwordResetStatus = action.payload.message;
        state.loading = false;
      })
      .addCase(passwordReset.rejected, (state, action) => {
        state.passwordResetStatus = null;
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getCurrentUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCurrentUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        // MERGE, never replace. This endpoint returns only what a profile
        // screen needs — name, email, contact, role, permission — and knows
        // nothing about organizationId, isManager, isTeamLead, candidate_id,
        // employeeId, etc. that loginUser/fetchCurrentUser populate at sign-in.
        // `state.user = action.payload` (the previous code) overwrote the
        // whole object, so simply opening the Profile page silently wiped all
        // of those fields for the rest of the session: isMultiRole and
        // isManager both read user.roles / user.isManager, so the workspace
        // switcher and every manager-only sidebar tab vanished — recoverable
        // only by a full reload, which re-runs fetchCurrentUser from scratch
        // and repopulates the full object. Reported as "the dashboard switcher
        // bar disappears after visiting another page and coming back."
        state.user = { ...state.user, ...action.payload };

        // Same merge for localStorage — several places (SidebarMenu's
        // getIsManager among them) read localStorage.userDetails directly
        // rather than through this reducer's in-memory state, and would have
        // inherited the same corruption independent of Redux.
        const existing = (() => {
          try { return JSON.parse(localStorage.getItem('userDetails') || '{}'); }
          catch { return {}; }
        })();
        const userDetails = {
          ...existing,
          user_id: action.payload.user_id,
          name: action.payload.name,
          email: action.payload.email,
          contactNumber: action.payload.contactNumber,
          imageUrl: action.payload.imageUrl,
          role: action.payload.role,
          roles: action.payload.roles,
          permission: action.payload.permission,
          isEmailVerified: action.payload.isEmailVerified,
          isActive: action.payload.isActive,
          isDefaultPass: action.payload.isDefaultPass,
        };
        localStorage.setItem('userDetails', JSON.stringify(userDetails));
      })
      .addCase(getCurrentUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(getUserById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.loading = false;
        // You can choose to update the current user if it's the same user, or handle it differently
        const currentUserId = state.user?.user_id;
        const fetchedUserId = action.payload.user_id;

        if (currentUserId && String(currentUserId) === String(fetchedUserId)) {
          state.user = action.payload;
        }
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        const updatedUser = action.payload.userData || action.payload;
        const currentUserId = state.user?.user_id || state.user?.userId;
        const updatedUserId = updatedUser.user_id || updatedUser.userId;

        if (currentUserId && updatedUserId && String(currentUserId) === String(updatedUserId)) {
          // Same full-replace bug as getCurrentUserProfile.fulfilled above,
          // same fix: the update-profile response only carries the fields the
          // edit form submits, so replacing state.user outright dropped roles,
          // organizationId, isManager, etc. every time "Save Changes" ran. The
          // localStorage merge two lines below already got this right
          // (`...JSON.parse(localStorage...)`) — this brings the in-memory
          // Redux copy in line with it.
          state.user = { ...state.user, ...updatedUser };

          const newUser = {
            ...JSON.parse(localStorage.getItem('userDetails') || '{}'),
            name: updatedUser.name,
            email: updatedUser.email,
            contactNumber: updatedUser.contactNumber,
            imageUrl: updatedUser.imageUrl,
            role: updatedUser.role?.roleName,
          };

          localStorage.setItem('userDetails', JSON.stringify(newUser));
        }
      })

      .addCase(updateUser.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updatePasswordByUser.fulfilled, (state, action) => {
      })
      .addCase(selfUpdatePasswordByUser.fulfilled, (state, action) => {
        if (state.user) {
          state.user.isDefaultPass = false;
        }

        const existingDetailsRaw = localStorage.getItem('userDetails');
        if (existingDetailsRaw) {
          try {
            const userDetails = JSON.parse(existingDetailsRaw);
            userDetails.isDefaultPass = false;
            localStorage.setItem('userDetails', JSON.stringify(userDetails));
          } catch (e) { }
        }
      });
  },
});



export const { logout, tokenRefreshed, setPermissions, clearError, resetOtpState, clearResetStatus } = authSlice.actions;
export default authSlice.reducer;